// src/components/admin/SAMBatchImport.tsx
// Admin component for configuring and running SAM.gov batch imports
import React, { useState, useEffect } from 'react';
import {
  Download, Play, Settings, Calendar, Filter, Building2, Tag,
  AlertCircle, CheckCircle, Clock, RefreshCw, Database, Zap,
  ChevronDown, ChevronUp, FileText, TrendingUp, XCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { localBatchImport, testLocalSamConnection, hasSamApiKey } from '../../lib/sam-dev-api';
import { NaicsCategoryPicker } from './NaicsCategoryPicker';
import toast from 'react-hot-toast';

interface ImportConfig {
  batchSize: number;
  naicsCodes: string[];
  agencies: string[];
  responseDateStart: string;
  responseDateEnd: string;
  postedDateStart: string;
  postedDateEnd: string;
  keywords: string;
  setAsideTypes: string[];
  noticeTypes: string[];
  onlyActive: boolean;
  maxRecords: number;
}

interface ImportResult {
  success: boolean;
  totalRecords: number;
  importedRecords: number;
  failedRecords: number;
  skippedRecords: number;
  errors: string[];
  duration: number;
  batchesProcessed: number;
  jobId: string;
  // Resumability fields
  lastOffset?: number;
  hasMore?: boolean;
  canResume?: boolean;
  nextResumeOffset?: number;
}

interface ImportJob {
  job_id: string;
  config: ImportConfig;
  result: any;
  duration_ms: number;
  completed_at: string;
}

// NAICS codes are now loaded from the `naics_codes` table and grouped into the 20
// official sectors by NaicsCategoryPicker (replacing the old hardcoded short list).

// Common agencies
const COMMON_AGENCIES = [
  'Department of Defense',
  'Department of Veterans Affairs',
  'Department of Health and Human Services',
  'Department of Homeland Security',
  'General Services Administration',
  'Department of Agriculture',
  'Department of the Treasury',
  'Department of Transportation',
  'Department of Justice',
  'Environmental Protection Agency',
  'Department of Energy',
  'Department of Labor',
  'NASA',
  'Social Security Administration',
  'Department of State',
  'Department of the Interior',
  'Department of Commerce',
  'Department of Education',
  'Department of Housing and Urban Development',
];

// Set-Aside Types - comprehensive list matching SAM.gov API codes
// Reference: https://sam.gov/content/opportunity-set-aside
const SET_ASIDE_TYPES = [
  // Small Business Programs
  { code: 'SBA', label: '8(a) Business Development', category: 'SBA Programs' },
  { code: 'HUBZ', label: 'HUBZone Small Business', category: 'SBA Programs' },
  { code: 'SB', label: 'Small Business Set-Aside', category: 'Small Business' },
  { code: 'SBP', label: 'Small Business (Partial)', category: 'Small Business' },
  { code: 'SDB', label: 'Small Disadvantaged Business', category: 'Small Business' },

  // Veteran Programs
  { code: 'SDVOSBC', label: 'Service-Disabled Veteran-Owned (Competitive)', category: 'Veteran' },
  { code: 'SDVOSBS', label: 'Service-Disabled Veteran-Owned (Sole Source)', category: 'Veteran' },
  { code: 'VSA', label: 'Veteran-Owned Small Business', category: 'Veteran' },
  { code: 'VSB', label: 'Veteran-Owned (Sole Source)', category: 'Veteran' },

  // Women-Owned Programs
  { code: 'WOSB', label: 'Women-Owned Small Business', category: 'Women-Owned' },
  { code: 'WOSBSS', label: 'Women-Owned (Sole Source)', category: 'Women-Owned' },
  { code: 'EDWOSB', label: 'Economically Disadvantaged WOSB', category: 'Women-Owned' },
  { code: 'EDWOSBSS', label: 'EDWOSB (Sole Source)', category: 'Women-Owned' },

  // Native American / Tribal Programs
  { code: 'IEE', label: 'Indian Economic Enterprise', category: 'Tribal' },
  { code: 'ISBEE', label: 'Indian Small Business Economic Enterprise', category: 'Tribal' },
  { code: 'BICiv', label: 'Buy Indian (Civilian)', category: 'Tribal' },

  // Other Programs
  { code: 'RSB', label: 'Emerging Small Business (Reserved)', category: 'Other' },
  { code: 'LAS', label: 'Local Area Set-Aside', category: 'Other' },
  { code: 'SDAOSBC', label: 'AbilityOne (Competitive)', category: 'Other' },
  { code: 'SDAOSBS', label: 'AbilityOne (Sole Source)', category: 'Other' },

  // Competition Types
  { code: 'FS', label: 'Full and Open Competition', category: 'Competition' },
  { code: 'FSS', label: 'Full and Open (After Exclusion)', category: 'Competition' },
];

// SAM.gov notice types (ptype codes). RFIs are posted as Sources Sought or Special
// Notice, so the "RFI / Market Research" shortcut selects both. When nothing is
// selected the import defaults to the active opportunity types (incl. Special Notice).
const NOTICE_TYPES = [
  { code: 'o', label: 'Solicitation' },
  { code: 'k', label: 'Combined Synopsis/Solicitation' },
  { code: 'p', label: 'Presolicitation' },
  { code: 'r', label: 'Sources Sought' },
  { code: 's', label: 'Special Notice' },
  { code: 'i', label: 'Intent to Bundle' },
  { code: 'a', label: 'Award Notice' },
  { code: 'u', label: 'Justification (J&A)' },
  { code: 'g', label: 'Sale of Surplus Property' },
];
// Notice types that represent RFIs / market research on SAM.gov.
const RFI_NOTICE_TYPES = ['r', 's'];

export function SAMBatchImport() {
  const [config, setConfig] = useState<ImportConfig>({
    batchSize: 1000, // Max batch size for efficiency (SAM.gov ignores NAICS filter, need client-side filtering)
    naicsCodes: [],
    agencies: [],
    responseDateStart: '',
    responseDateEnd: '',
    postedDateStart: '',
    postedDateEnd: '',
    keywords: '',
    setAsideTypes: [],
    noticeTypes: [],
    onlyActive: true,
    maxRecords: 100 // Target number of matching contracts to import
  });

  const [isRunning, setIsRunning] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const [enrichResult, setEnrichResult] = useState<any>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [recentJobs, setRecentJobs] = useState<ImportJob[]>([]);
  const [contractStats, setContractStats] = useState<any>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  // Resumable import state
  const [cumulativeImported, setCumulativeImported] = useState(0);
  const [isAutoResuming, setIsAutoResuming] = useState(false);

  // Load recent jobs and contract stats on mount
  useEffect(() => {
    loadRecentJobs();
    loadContractStats();
  }, []);

  const addLog = (message: string) => {
    setLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const loadRecentJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('import_jobs')
        .select('*')
        .order('completed_at', { ascending: false })
        .limit(5);

      if (!error && data) {
        setRecentJobs(data);
      }
    } catch (err) {
      console.log('Import jobs table may not exist yet');
    }
  };

  const loadContractStats = async () => {
    try {
      const { data, error, count } = await supabase
        .from('sam_contracts')
        .select('status, active', { count: 'exact' });

      if (!error && data) {
        const stats = {
          total: count || 0,
          active: data.filter(c => c.active).length,
          open: data.filter(c => c.status === 'open').length,
          pending: data.filter(c => c.status === 'pending').length,
          closed: data.filter(c => c.status === 'closed').length
        };
        setContractStats(stats);
      }
    } catch (err) {
      console.error('Error loading contract stats:', err);
    }
  };

  const handleRunImport = async (startOffset: number = 0, resumeJobId?: string) => {
    setIsRunning(true);

    // Only reset logs and cumulative count for fresh imports (not resumes)
    if (startOffset === 0) {
      setResult(null);
      setLogs([]);
      setCumulativeImported(0);
      addLog('Starting SAM.gov batch import...');
    } else {
      addLog(`🔄 Resuming import from offset ${startOffset}...`);
    }

    try {
      addLog(`Configuration: Batch size ${config.batchSize}, Max records ${config.maxRecords}`);
      if (config.naicsCodes.length > 0) {
        addLog(`NAICS filter: ${config.naicsCodes.join(', ')}`);
      }
      if (config.agencies.length > 0) {
        addLog(`Agency filter: ${config.agencies.join(', ')}`);
      }
      if (startOffset > 0) {
        addLog(`Starting from offset: ${startOffset}`);
      }

      let importResult: ImportResult;

      // Use local API mode if enabled
      if (useLocalApi) {
        addLog('📡 Using local API mode (direct SAM.gov connection via Vite proxy)...');
        addLog('⚠️ Note: Records are fetched but NOT saved to database in local mode');

        importResult = await localBatchImport({
          batchSize: config.batchSize,
          naicsCodes: config.naicsCodes.length > 0 ? config.naicsCodes : undefined,
          agencies: config.agencies.length > 0 ? config.agencies : undefined,
          responseDateStart: config.responseDateStart || undefined,
          responseDateEnd: config.responseDateEnd || undefined,
          postedDateStart: config.postedDateStart || undefined,
          postedDateEnd: config.postedDateEnd || undefined,
          keywords: config.keywords || undefined,
          setAsideTypes: config.setAsideTypes.length > 0 ? config.setAsideTypes : undefined,
          noticeTypes: config.noticeTypes.length > 0 ? config.noticeTypes : undefined,
          onlyActive: config.onlyActive,
          maxRecords: config.maxRecords,
          startOffset
        });
      } else {
        // Use Supabase edge function
        addLog('Calling edge function: batch-import-sam...');
        console.log('🚀 Invoking batch-import-sam edge function...');
        console.log('📦 Request body:', {
          batchSize: config.batchSize,
          naicsCodes: config.naicsCodes.length > 0 ? config.naicsCodes : null,
          agencies: config.agencies.length > 0 ? config.agencies : null,
          maxRecords: config.maxRecords,
          startOffset
        });

        // Add a visual indicator that we're waiting
        addLog('⏳ Waiting for response (this may take 30+ seconds)...');

        const response = await supabase.functions.invoke('batch-import-sam', {
          body: {
            batchSize: config.batchSize,
            naicsCodes: config.naicsCodes.length > 0 ? config.naicsCodes : null,
            agencies: config.agencies.length > 0 ? config.agencies : null,
            responseDateStart: config.responseDateStart || null,
            responseDateEnd: config.responseDateEnd || null,
            postedDateStart: config.postedDateStart || null,
            postedDateEnd: config.postedDateEnd || null,
            keywords: config.keywords || null,
            setAsideTypes: config.setAsideTypes.length > 0 ? config.setAsideTypes : null,
            noticeTypes: config.noticeTypes.length > 0 ? config.noticeTypes : null,
            onlyActive: config.onlyActive,
            maxRecords: config.maxRecords,
            startOffset,
            resumeJobId
          }
        });

        console.log('📡 Edge function response:', response);
        addLog(`Edge function responded`);

        // Check for error in response
        if (response.error) {
          console.error('❌ Edge function error:', response.error);
          throw new Error(response.error.message || 'Edge function returned an error');
        }

        // Check if we got any data back
        if (!response.data) {
          console.error('❌ No data returned from edge function');
          addLog('ERROR: Edge function returned no data. The function may not be deployed.');
          addLog('To deploy: Run "supabase functions deploy batch-import-sam" in your terminal');
          addLog('Make sure SAM_API_KEY is set in your Supabase project secrets');
          throw new Error('Edge function returned no data. It may not be deployed or SAM_API_KEY may not be configured.');
        }

        // Check if response indicates an error
        if (response.data.error) {
          console.error('❌ Import error:', response.data.error);
          throw new Error(response.data.error);
        }

        importResult = response.data as ImportResult;
      }
      setResult(importResult);

      // Update cumulative count
      const newCumulative = cumulativeImported + importResult.importedRecords;
      setCumulativeImported(newCumulative);

      if (importResult.success) {
        addLog(`Import batch completed successfully!`);
        addLog(`Total records available: ${importResult.totalRecords}`);
        addLog(`Records imported this batch: ${importResult.importedRecords}`);
        addLog(`Total imported so far: ${newCumulative}`);
        addLog(`Records failed: ${importResult.failedRecords}`);
        addLog(`Batches processed: ${importResult.batchesProcessed}`);
        addLog(`Duration: ${(importResult.duration / 1000).toFixed(1)}s`);

        // Show resume info if available
        if (importResult.canResume) {
          addLog(`🔄 More records available - can resume from offset ${importResult.nextResumeOffset}`);
          addLog(`📊 Progress: ${importResult.nextResumeOffset} / ${importResult.totalRecords} records scanned`);
        }

        toast.success(`Imported ${importResult.importedRecords} contracts from SAM.gov`);
      } else {
        addLog(`Import completed with errors`);
        importResult.errors.forEach(err => addLog(`Error: ${err}`));
        toast.error('Import completed with some errors');
      }

      // Refresh data
      loadRecentJobs();
      loadContractStats();

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Import failed: ${message}`);
      toast.error(`Import failed: ${message}`);
      setResult({
        success: false,
        totalRecords: 0,
        importedRecords: 0,
        failedRecords: 0,
        skippedRecords: 0,
        errors: [message],
        duration: 0,
        batchesProcessed: 0,
        jobId: ''
      });
    } finally {
      setIsRunning(false);
      setIsAutoResuming(false);
    }
  };

  // Auto-resume handler for continuous import
  const handleAutoResume = async () => {
    if (!result?.canResume || !result.nextResumeOffset) {
      toast.error('Cannot resume - no resume point available');
      return;
    }

    setIsAutoResuming(true);
    await handleRunImport(result.nextResumeOffset, result.jobId);
  };

  // Cumulative enrichment tracking
  const [cumulativeEnriched, setCumulativeEnriched] = useState(0);

  // Batch enrich existing contracts
  const handleBatchEnrich = async (forceRefresh: boolean = false, startOffset: number = 0) => {
    setIsEnriching(true);

    // Only reset for fresh starts
    if (startOffset === 0) {
      setEnrichResult(null);
      setCumulativeEnriched(0);
      addLog('Starting batch enrichment of existing contracts...');
    } else {
      addLog(`🔄 Resuming enrichment from offset ${startOffset}...`);
    }

    try {
      addLog(`Enriching contracts (forceRefresh: ${forceRefresh})...`);
      addLog('⏳ This may take several minutes...');

      const response = await supabase.functions.invoke('batch-enrich-contracts', {
        body: {
          limit: 100,
          forceRefresh,
          onlyUrlDescriptions: !forceRefresh,
          startOffset
        }
      });

      console.log('Enrichment response:', response);

      if (response.error) {
        throw new Error(response.error.message || 'Enrichment failed');
      }

      if (!response.data) {
        addLog('ERROR: No response from enrichment function');
        addLog('Deploy the function: supabase functions deploy batch-enrich-contracts');
        throw new Error('Enrichment function not deployed');
      }

      setEnrichResult(response.data);

      // Update cumulative count
      const newCumulative = cumulativeEnriched + response.data.enriched;
      setCumulativeEnriched(newCumulative);

      if (response.data.success) {
        addLog(`Enrichment batch completed!`);
        addLog(`Processed: ${response.data.totalProcessed}`);
        addLog(`Enriched this batch: ${response.data.enriched}`);
        if (newCumulative > response.data.enriched) {
          addLog(`Total enriched: ${newCumulative}`);
        }
        addLog(`Skipped: ${response.data.skipped}`);
        addLog(`Failed: ${response.data.failed}`);
        addLog(`Duration: ${(response.data.duration / 1000).toFixed(1)}s`);

        if (response.data.canResume) {
          addLog(`🔄 More contracts available - can resume from offset ${response.data.nextResumeOffset}`);
          addLog(`📊 Progress: ${response.data.lastOffset} / ${response.data.totalAvailable} contracts`);
        }

        toast.success(`Enriched ${response.data.enriched} contracts with full descriptions and documents`);
      } else {
        addLog('Enrichment completed with errors');
        response.data.errors?.forEach((err: string) => addLog(`Error: ${err}`));
        toast.error('Enrichment completed with some errors');
      }

      // Refresh stats
      loadContractStats();

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      addLog(`Enrichment failed: ${message}`);
      toast.error(`Enrichment failed: ${message}`);
    } finally {
      setIsEnriching(false);
    }
  };

  // Resume enrichment from last offset
  const handleResumeEnrich = async () => {
    if (!enrichResult?.canResume || !enrichResult.nextResumeOffset) {
      toast.error('Cannot resume - no resume point available');
      return;
    }
    await handleBatchEnrich(false, enrichResult.nextResumeOffset);
  };

  const handleNaicsToggle = (code: string) => {
    setConfig(prev => ({
      ...prev,
      naicsCodes: prev.naicsCodes.includes(code)
        ? prev.naicsCodes.filter(c => c !== code)
        : [...prev.naicsCodes, code]
    }));
  };

  const handleAgencyToggle = (agency: string) => {
    setConfig(prev => ({
      ...prev,
      agencies: prev.agencies.includes(agency)
        ? prev.agencies.filter(a => a !== agency)
        : [...prev.agencies, agency]
    }));
  };

  const handleSetAsideToggle = (code: string) => {
    setConfig(prev => ({
      ...prev,
      setAsideTypes: prev.setAsideTypes.includes(code)
        ? prev.setAsideTypes.filter(c => c !== code)
        : [...prev.setAsideTypes, code]
    }));
  };

  const handleNoticeTypeToggle = (code: string) => {
    setConfig(prev => ({
      ...prev,
      noticeTypes: prev.noticeTypes.includes(code)
        ? prev.noticeTypes.filter(c => c !== code)
        : [...prev.noticeTypes, code]
    }));
  };

  // One-click "RFI / Market Research" = Sources Sought + Special Notice. Toggles the
  // pair on/off together.
  const handleRfiPreset = () => {
    setConfig(prev => {
      const hasAll = RFI_NOTICE_TYPES.every(c => prev.noticeTypes.includes(c));
      return {
        ...prev,
        noticeTypes: hasAll
          ? prev.noticeTypes.filter(c => !RFI_NOTICE_TYPES.includes(c))
          : Array.from(new Set([...prev.noticeTypes, ...RFI_NOTICE_TYPES]))
      };
    });
  };

  // Test connection to edge function
  const [connectionStatus, setConnectionStatus] = useState<'unknown' | 'testing' | 'connected' | 'error'>('unknown');

  // Development mode - use local API proxy instead of edge functions
  const [useLocalApi, setUseLocalApi] = useState(false);
  const [localApiAvailable, setLocalApiAvailable] = useState(hasSamApiKey());

  const handleTestConnection = async () => {
    setConnectionStatus('testing');

    // If using local API, test that instead
    if (useLocalApi) {
      addLog('Testing local SAM.gov API connection...');
      try {
        const result = await testLocalSamConnection();
        if (result.success) {
          setConnectionStatus('connected');
          addLog(`✅ ${result.message}`);
          toast.success('Local API connected');
        } else {
          setConnectionStatus('error');
          addLog(`❌ ${result.message}`);
          toast.error(result.message);
        }
      } catch (error) {
        setConnectionStatus('error');
        const message = error instanceof Error ? error.message : 'Unknown error';
        addLog(`❌ Local API test failed: ${message}`);
        toast.error('Local API test failed');
      }
      return;
    }

    addLog('Testing connection to batch-import-sam edge function...');
    addLog('Using health check mode (fast response)...');

    try {
      // Create a timeout promise - increased to 30 seconds for cold starts
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('TIMEOUT')), 30000); // 30 second timeout
      });

      // Race between the API call and timeout
      // Use health check mode for fast response (doesn't call SAM.gov)
      const response = await Promise.race([
        supabase.functions.invoke('batch-import-sam', {
          body: {
            healthCheck: true
          }
        }),
        timeoutPromise
      ]);

      console.log('Health check response:', response);

      if (response.error) {
        setConnectionStatus('error');
        const errorMsg = response.error.message || 'Unknown error';
        addLog(`Connection test FAILED: ${errorMsg}`);

        // Check for specific error types
        if (errorMsg.includes('FunctionsFetchError') || errorMsg.includes('Failed to fetch')) {
          addLog('');
          addLog('⚠️  Could not reach the edge function.');
          addLog('This usually means the function is not deployed.');
          addLog('');
          addLog('To deploy, run in your terminal:');
          addLog('  supabase functions deploy batch-import-sam');
        } else {
          addLog('Make sure the edge function is deployed: supabase functions deploy batch-import-sam');
        }
        toast.error('Edge function not available');
        return;
      }

      if (!response.data) {
        setConnectionStatus('error');
        addLog('Connection test FAILED: No response from edge function');
        addLog('The edge function may not be deployed. Run: supabase functions deploy batch-import-sam');
        toast.error('Edge function not deployed');
        return;
      }

      // Handle health check response
      if (response.data.healthCheck) {
        if (response.data.success) {
          setConnectionStatus('connected');
          addLog(`✅ ${response.data.message}`);
          addLog(`Configuration status:`);
          addLog(`  - SAM_API_KEY: ${response.data.config?.samApiKey || 'unknown'}`);
          addLog(`  - SUPABASE_URL: ${response.data.config?.supabaseUrl || 'unknown'}`);
          addLog(`  - SUPABASE_SERVICE_ROLE_KEY: ${response.data.config?.supabaseKey || 'unknown'}`);
          toast.success('Edge function connected and configured');
        } else {
          setConnectionStatus('error');
          addLog(`⚠️ Edge function is deployed but has configuration issues:`);
          addLog(`  ${response.data.message}`);
          if (response.data.config?.samApiKey === 'missing') {
            addLog('');
            addLog('To fix, run: supabase secrets set SAM_API_KEY=your_sam_api_key');
            addLog('Get a SAM.gov API key at: https://api.sam.gov');
          }
          toast.error('Edge function needs configuration');
        }
        return;
      }

      // Legacy response handling (if edge function hasn't been updated yet)
      if (response.data.error && response.data.error.includes('SAM_API_KEY')) {
        setConnectionStatus('error');
        addLog('Connection test FAILED: SAM_API_KEY not configured');
        addLog('Set the SAM_API_KEY in Supabase project secrets');
        toast.error('SAM_API_KEY not configured');
        return;
      }

      setConnectionStatus('connected');
      addLog('Connection test PASSED - Edge function is responding');
      toast.success('Edge function connected successfully');
    } catch (error) {
      console.error('Connection test error:', error);
      setConnectionStatus('error');
      const message = error instanceof Error ? error.message : 'Unknown error';

      if (message === 'TIMEOUT') {
        addLog('');
        addLog('⚠️  CONNECTION TIMEOUT after 30 seconds');
        addLog('');
        addLog('This usually means the edge function is NOT DEPLOYED to Supabase.');
        addLog('');
        addLog('To fix, run these commands in your terminal:');
        addLog('');
        addLog('  1. supabase login');
        addLog('  2. supabase link --project-ref YOUR_PROJECT_REF');
        addLog('  3. supabase functions deploy batch-import-sam');
        addLog('  4. supabase secrets set SAM_API_KEY=your_sam_api_key');
        addLog('');
        addLog('Get a SAM.gov API key at: https://api.sam.gov');
        addLog('');
        // Check if local API is available as alternative
        if (localApiAvailable) {
          addLog('💡 TIP: You can enable "Use Local API" mode to test without edge functions.');
          addLog('   This uses the Vite dev server proxy with VITE_SAM_API_KEY from .env');
        }
        toast.error('Edge function not responding (timeout)');
      } else {
        addLog(`Connection test ERROR: ${message}`);
        toast.error('Connection test failed');
      }
    }
  };

  // Auto-test connection on mount (only if not using local API)
  useEffect(() => {
    if (!useLocalApi && connectionStatus === 'unknown') {
      // Delay slightly to avoid UI flash
      const timer = setTimeout(() => {
        handleTestConnection();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [useLocalApi]);

  // Calculate default dates
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const ninetyDaysFromNow = new Date(today);
    ninetyDaysFromNow.setDate(ninetyDaysFromNow.getDate() + 90);

    return {
      postedFrom: thirtyDaysAgo.toISOString().split('T')[0],
      postedTo: today.toISOString().split('T')[0],
      responseFrom: today.toISOString().split('T')[0],
      responseTo: ninetyDaysFromNow.toISOString().split('T')[0]
    };
  };

  const defaults = getDefaultDates();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-bw-ink">SAM.gov Batch Import</h2>
          <p className="text-bw-muted mt-1">
            Import federal contract opportunities from SAM.gov
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleTestConnection}
            disabled={connectionStatus === 'testing'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium border transition-all ${
              connectionStatus === 'connected'
                ? 'bg-success-bg border-green-300 text-success'
                : connectionStatus === 'error'
                ? 'bg-urgent-bg border-red-300 text-urgent'
                : 'bg-bw-surface-subtle border-bw-border text-bw-ink hover:bg-bw-surface-subtle'
            }`}
          >
            {connectionStatus === 'testing' ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : connectionStatus === 'connected' ? (
              <>
                <CheckCircle className="h-4 w-4" />
                Connected
              </>
            ) : connectionStatus === 'error' ? (
              <>
                <XCircle className="h-4 w-4" />
                Not Connected
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" />
                Test Connection
              </>
            )}
          </button>
          <button
            onClick={() => handleRunImport()}
            disabled={isRunning}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-all ${
              isRunning
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-bw-primary hover:bg-servet-dark-blue'
            }`}
          >
            {isRunning ? (
              <>
                <RefreshCw className="h-5 w-5 animate-spin" />
                Importing...
              </>
            ) : (
            <>
              <Download className="h-5 w-5" />
              Run Import
            </>
          )}
        </button>
        </div>
      </div>

      {/* Connection Status Banner */}
      {!useLocalApi && (
        <>
          {/* Testing Banner */}
          {connectionStatus === 'testing' && (
            <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <RefreshCw className="h-5 w-5 text-bw-primary animate-spin flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-bw-primary">Testing Connection...</h4>
                  <p className="text-sm text-bw-primary">Checking if the edge function is deployed and configured.</p>
                </div>
              </div>
            </div>
          )}

          {/* Connected Banner */}
          {connectionStatus === 'connected' && (
            <div className="bg-success-bg border border-success-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-success">Edge Function Ready</h4>
                  <p className="text-sm text-success">The SAM.gov import function is deployed and configured.</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Banner - shows when not connected */}
          {connectionStatus === 'error' && (
            <div className="bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-fit-moderate flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-fit-moderate">Edge Function Issue Detected</h4>
                  <p className="text-sm text-fit-moderate mt-1">
                    The SAM.gov import function may not be deployed or configured. Check the logs below for details.
                  </p>
                  <div className="mt-2 bg-fit-moderate-bg rounded p-3 font-mono text-xs text-yellow-900 space-y-1">
                    <div># 1. Deploy the edge function</div>
                    <div className="font-semibold">supabase functions deploy batch-import-sam</div>
                    <div className="mt-2"># 2. Set your SAM.gov API key as a secret</div>
                    <div className="font-semibold">supabase secrets set SAM_API_KEY=your_sam_api_key</div>
                  </div>
                  <p className="text-xs text-fit-moderate mt-2">
                    Get a free SAM.gov API key at{' '}
                    <a
                      href="https://api.sam.gov/docs/home"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline hover:text-fit-moderate"
                    >
                      api.sam.gov
                    </a>
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Local API Mode Toggle */}
      <div className="bg-bw-surface rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${useLocalApi ? 'bg-success-bg' : 'bg-bw-surface-subtle'}`}>
              <Zap className={`h-5 w-5 ${useLocalApi ? 'text-success' : 'text-bw-muted'}`} />
            </div>
            <div>
              <h4 className="font-medium text-bw-ink">Local API Mode (Development)</h4>
              <p className="text-sm text-bw-muted">
                {useLocalApi
                  ? 'Using Vite dev server proxy to call SAM.gov directly'
                  : 'Using Supabase edge function (requires deployment)'
                }
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setUseLocalApi(!useLocalApi);
              setConnectionStatus('unknown');
            }}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-bw-primary focus:ring-offset-2 ${
              useLocalApi ? 'bg-success' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-bw-surface shadow ring-0 transition duration-200 ease-in-out ${
                useLocalApi ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        {useLocalApi && (
          <div className="mt-3 p-3 bg-success-bg rounded-lg border border-green-200">
            <p className="text-sm text-success">
              <strong>Note:</strong> Local mode fetches data from SAM.gov but does NOT save to the database.
              Use this for testing API connectivity. For production import, deploy the edge function.
            </p>
            {!localApiAvailable && (
              <p className="text-sm text-fit-moderate mt-2">
                <strong>Warning:</strong> VITE_SAM_API_KEY not found in environment.
                Add it to your .env file to use local mode.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Current Stats */}
      {contractStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-bw-surface rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-bw-muted text-sm">
              <Database className="h-4 w-4" />
              Total Contracts
            </div>
            <div className="text-2xl font-bold mt-1">{contractStats.total}</div>
          </div>
          <div className="bg-bw-surface rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-success text-sm">
              <Zap className="h-4 w-4" />
              Active
            </div>
            <div className="text-2xl font-bold mt-1 text-success">{contractStats.active}</div>
          </div>
          <div className="bg-bw-surface rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-bw-primary text-sm">
              <CheckCircle className="h-4 w-4" />
              Open
            </div>
            <div className="text-2xl font-bold mt-1 text-bw-primary">{contractStats.open}</div>
          </div>
          <div className="bg-bw-surface rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-fit-moderate text-sm">
              <Clock className="h-4 w-4" />
              Pending
            </div>
            <div className="text-2xl font-bold mt-1 text-fit-moderate">{contractStats.pending}</div>
          </div>
          <div className="bg-bw-surface rounded-lg shadow p-4">
            <div className="flex items-center gap-2 text-urgent text-sm">
              <XCircle className="h-4 w-4" />
              Closed
            </div>
            <div className="text-2xl font-bold mt-1 text-urgent">{contractStats.closed}</div>
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-bw-surface rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-bw-primary" />
          Import Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Batch Size */}
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Batch Size
            </label>
            <input
              type="number"
              min="10"
              max="1000"
              value={config.batchSize}
              onChange={(e) => setConfig(prev => ({ ...prev, batchSize: parseInt(e.target.value) || 300 }))}
              className="w-full px-4 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
            />
            <p className="text-xs text-bw-muted mt-1">Records per API call (max 1000)</p>
          </div>

          {/* Max Records */}
          <div>
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Maximum Records
            </label>
            <input
              type="number"
              min="100"
              max="10000"
              value={config.maxRecords}
              onChange={(e) => setConfig(prev => ({ ...prev, maxRecords: parseInt(e.target.value) || 1000 }))}
              className="w-full px-4 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
            />
            <p className="text-xs text-bw-muted mt-1">Total records to import (max 10000)</p>
          </div>

          {/* Keywords */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-bw-ink mb-1">
              Keywords (optional)
            </label>
            <input
              type="text"
              value={config.keywords}
              onChange={(e) => setConfig(prev => ({ ...prev, keywords: e.target.value }))}
              placeholder="e.g., software development, cybersecurity, cloud"
              className="w-full px-4 py-2 border border-bw-border rounded-lg focus:ring-2 focus:ring-bw-primary focus:border-transparent"
            />
          </div>

          {/* Active Only Toggle */}
          <div className="md:col-span-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.onlyActive}
                onChange={(e) => setConfig(prev => ({ ...prev, onlyActive: e.target.checked }))}
                className="w-4 h-4 text-bw-primary rounded focus:ring-bw-primary"
              />
              <span className="text-sm font-medium text-bw-ink">
                Only import active solicitations (recommended)
              </span>
            </label>
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="mt-6 flex items-center gap-2 text-bw-primary hover:text-servet-dark-blue font-medium"
        >
          {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
        </button>

        {showAdvanced && (
          <div className="mt-6 space-y-6 pt-6 border-t">
            {/* Date Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-bw-ink mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Posted Date Range
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-bw-muted mb-1">From</label>
                    <input
                      type="date"
                      value={config.postedDateStart}
                      onChange={(e) => setConfig(prev => ({ ...prev, postedDateStart: e.target.value }))}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-bw-muted mb-1">To</label>
                    <input
                      type="date"
                      value={config.postedDateEnd}
                      onChange={(e) => setConfig(prev => ({ ...prev, postedDateEnd: e.target.value }))}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-bw-muted mt-1">Default: Last 90 days</p>
              </div>

              <div>
                <h4 className="font-medium text-bw-ink mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Response Deadline Range
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-bw-muted mb-1">From</label>
                    <input
                      type="date"
                      value={config.responseDateStart}
                      onChange={(e) => setConfig(prev => ({ ...prev, responseDateStart: e.target.value }))}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-bw-muted mb-1">To</label>
                    <input
                      type="date"
                      value={config.responseDateEnd}
                      onChange={(e) => setConfig(prev => ({ ...prev, responseDateEnd: e.target.value }))}
                      className="w-full px-3 py-2 border border-bw-border rounded-lg text-sm"
                    />
                  </div>
                </div>
                <p className="text-xs text-bw-muted mt-1">Filter by due date</p>
              </div>
            </div>

            {/* Notice Types (RFI / Solicitation / Sources Sought / etc.) */}
            <div>
              <h4 className="font-medium text-bw-ink mb-3 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Notice Type
                {config.noticeTypes.length > 0 && (
                  <span className="text-xs bg-bw-primary text-white px-2 py-0.5 rounded-full">
                    {config.noticeTypes.length} selected
                  </span>
                )}
              </h4>
              <div className="mb-2">
                <button
                  onClick={handleRfiPreset}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    RFI_NOTICE_TYPES.every(c => config.noticeTypes.includes(c))
                      ? 'bg-bw-primary text-white'
                      : 'bg-bw-primary-tint text-bw-primary border border-bw-primary/30 hover:bg-bw-primary/10'
                  }`}
                  title="RFIs are posted as Sources Sought or Special Notice"
                >
                  ✨ RFI / Market Research (Sources Sought + Special Notice)
                </button>
              </div>
              <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-bw-surface-subtle">
                {NOTICE_TYPES.map(nt => (
                  <button
                    key={nt.code}
                    onClick={() => handleNoticeTypeToggle(nt.code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      config.noticeTypes.includes(nt.code)
                        ? 'bg-bw-primary text-white'
                        : 'bg-bw-surface border border-bw-border text-bw-ink hover:border-servet-blue'
                    }`}
                  >
                    {nt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-bw-muted mt-1">
                Leave empty for the default active set (Solicitation, Combined, Presolicitation, Sources Sought, Special Notice). Select to narrow — e.g. RFIs only.
              </p>
            </div>

            {/* NAICS Codes */}
            <div>
              <h4 className="font-medium text-bw-ink mb-3 flex items-center gap-2">
                <Tag className="h-4 w-4" />
                NAICS Codes
                {config.naicsCodes.length > 0 && (
                  <span className="text-xs bg-bw-primary text-white px-2 py-0.5 rounded-full">
                    {config.naicsCodes.length} selected
                  </span>
                )}
              </h4>
              <NaicsCategoryPicker selected={config.naicsCodes} onToggle={handleNaicsToggle} />
            </div>

            {/* Agencies */}
            <div>
              <h4 className="font-medium text-bw-ink mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Agencies
                {config.agencies.length > 0 && (
                  <span className="text-xs bg-bw-primary text-white px-2 py-0.5 rounded-full">
                    {config.agencies.length} selected
                  </span>
                )}
              </h4>
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg bg-bw-surface-subtle">
                {COMMON_AGENCIES.map(agency => (
                  <button
                    key={agency}
                    onClick={() => handleAgencyToggle(agency)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      config.agencies.includes(agency)
                        ? 'bg-bw-primary text-white'
                        : 'bg-bw-surface border border-bw-border text-bw-ink hover:border-servet-blue'
                    }`}
                  >
                    {agency.replace('Department of ', '')}
                  </button>
                ))}
              </div>
            </div>

            {/* Set-Aside Types */}
            <div>
              <h4 className="font-medium text-bw-ink mb-3 flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Set-Aside Types
                {config.setAsideTypes.length > 0 && (
                  <span className="text-xs bg-bw-primary text-white px-2 py-0.5 rounded-full">
                    {config.setAsideTypes.length} selected
                  </span>
                )}
              </h4>
              {/* Group set-asides by category */}
              <div className="space-y-4">
                {['SBA Programs', 'Small Business', 'Veteran', 'Women-Owned', 'Tribal', 'Other', 'Competition'].map(category => {
                  const categoryTypes = SET_ASIDE_TYPES.filter(t => t.category === category);
                  if (categoryTypes.length === 0) return null;
                  return (
                    <div key={category}>
                      <p className="text-xs font-semibold text-bw-muted uppercase tracking-wide mb-2">{category}</p>
                      <div className="flex flex-wrap gap-2">
                        {categoryTypes.map(type => (
                          <button
                            key={type.code}
                            onClick={() => handleSetAsideToggle(type.code)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              config.setAsideTypes.includes(type.code)
                                ? 'bg-bw-primary text-white'
                                : 'bg-bw-surface border border-bw-border text-bw-ink hover:border-servet-blue'
                            }`}
                            title={`${type.label} (${type.code})`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className={`rounded-xl shadow-lg p-6 ${
          result.success ? 'bg-success-bg border border-success-border' : 'bg-urgent-bg border border-urgent-border'
        }`}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            {result.success ? (
              <CheckCircle className="h-5 w-5 text-success" />
            ) : (
              <AlertCircle className="h-5 w-5 text-urgent" />
            )}
            Import Results
            {result.canResume && (
              <span className="ml-2 text-xs bg-bw-primary-tint text-bw-primary px-2 py-1 rounded-full">
                Can Resume
              </span>
            )}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="bg-bw-surface rounded-lg p-3">
              <div className="text-sm text-bw-muted">Total Available</div>
              <div className="text-xl font-bold">{result.totalRecords.toLocaleString()}</div>
            </div>
            <div className="bg-bw-surface rounded-lg p-3">
              <div className="text-sm text-success">Imported (This Batch)</div>
              <div className="text-xl font-bold text-success">{result.importedRecords.toLocaleString()}</div>
            </div>
            {cumulativeImported > result.importedRecords && (
              <div className="bg-bw-surface rounded-lg p-3">
                <div className="text-sm text-bw-primary">Total Imported</div>
                <div className="text-xl font-bold text-bw-primary">{cumulativeImported.toLocaleString()}</div>
              </div>
            )}
            <div className="bg-bw-surface rounded-lg p-3">
              <div className="text-sm text-fit-moderate">Filtered Out</div>
              <div className="text-xl font-bold text-fit-moderate">{(result.skippedRecords || 0).toLocaleString()}</div>
            </div>
            <div className="bg-bw-surface rounded-lg p-3">
              <div className="text-sm text-urgent">Failed</div>
              <div className="text-xl font-bold text-urgent">{result.failedRecords}</div>
            </div>
            <div className="bg-bw-surface rounded-lg p-3">
              <div className="text-sm text-bw-muted">Duration</div>
              <div className="text-xl font-bold">{(result.duration / 1000).toFixed(1)}s</div>
            </div>
          </div>

          {/* Honest coverage summary — explains "why did I only get N" */}
          <div className="bg-bw-surface rounded-lg p-3 mb-4 text-sm text-bw-ink">
            <span className="font-medium">Coverage: </span>
            scanned {(result.lastOffset || 0).toLocaleString()} of {result.totalRecords.toLocaleString()} available · imported {result.importedRecords.toLocaleString()} · filtered out {(result.skippedRecords || 0).toLocaleString()}
            {(config.naicsCodes.length > 0) && (
              <span className="text-bw-muted"> (SAM.gov ignores the NAICS param, so codes are matched client-side — many scanned records won't match your selection)</span>
            )}
            {result.canResume
              ? <span className="text-bw-primary font-medium"> · more available — use Resume to keep scanning.</span>
              : <span className="text-success font-medium"> · scan complete for these filters.</span>}
          </div>

          {/* Progress bar for resumable imports */}
          {result.totalRecords > 0 && result.lastOffset !== undefined && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-bw-muted mb-1">
                <span>Progress: {result.lastOffset.toLocaleString()} / {result.totalRecords.toLocaleString()} records scanned</span>
                <span>{Math.round((result.lastOffset / result.totalRecords) * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-bw-primary transition-all duration-300"
                  style={{ width: `${Math.min((result.lastOffset / result.totalRecords) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}

          {/* Resume Import Button */}
          {result.canResume && (
            <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-bw-primary">More records available</h4>
                  <p className="text-sm text-bw-primary mt-1">
                    Import stopped to avoid timeout. {(result.totalRecords - (result.nextResumeOffset || 0)).toLocaleString()} records remaining to scan.
                  </p>
                </div>
                <button
                  onClick={handleAutoResume}
                  disabled={isRunning || isAutoResuming}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    isRunning || isAutoResuming
                      ? 'bg-gray-300 cursor-not-allowed text-bw-muted'
                      : 'bg-bw-primary hover:brightness-110 text-white'
                  }`}
                >
                  {isAutoResuming ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Resuming...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Resume Import
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {result.errors.length > 0 && (
            <div className="bg-bw-surface rounded-lg p-4 mt-4">
              <h4 className="font-medium text-urgent mb-2">Errors ({result.errors.length})</h4>
              <div className="max-h-32 overflow-y-auto text-sm text-bw-muted">
                {result.errors.slice(0, 10).map((error, i) => (
                  <div key={i} className="py-1 border-b border-bw-border last:border-0">
                    {error}
                  </div>
                ))}
                {result.errors.length > 10 && (
                  <div className="text-bw-muted mt-2">
                    ... and {result.errors.length - 10} more errors
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Batch Enrichment Section */}
      <div className="bg-bw-surface rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-bw-primary" />
          Enrich Existing Contracts
        </h3>
        <p className="text-bw-muted mb-4">
          Fetch full descriptions and attachments from SAM.gov for contracts already imported.
          This updates contracts that only have URL links or minimal descriptions.
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={() => handleBatchEnrich(false)}
            disabled={isEnriching || isRunning}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              isEnriching || isRunning
                ? 'bg-gray-300 cursor-not-allowed text-bw-muted'
                : 'bg-bw-primary hover:brightness-110 text-white'
            }`}
          >
            {isEnriching ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Enriching...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Enrich New Contracts
              </>
            )}
          </button>

          <button
            onClick={() => handleBatchEnrich(true)}
            disabled={isEnriching || isRunning}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all border ${
              isEnriching || isRunning
                ? 'bg-bw-surface-subtle cursor-not-allowed text-bw-muted border-bw-border'
                : 'bg-bw-surface hover:bg-bw-primary-tint text-bw-primary border-purple-300 hover:border-purple-400'
            }`}
          >
            <RefreshCw className="h-4 w-4" />
            Force Re-Enrich All
          </button>
        </div>

        <p className="text-xs text-bw-muted">
          "Enrich New" only processes contracts not yet enriched. "Force Re-Enrich" updates all contracts including previously enriched ones.
        </p>

        {/* Enrichment Results */}
        {enrichResult && (
          <div className={`mt-4 rounded-lg p-4 ${
            enrichResult.success ? 'bg-bw-primary-tint border border-bw-primary/20' : 'bg-urgent-bg border border-urgent-border'
          }`}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-3">
              <div>
                <div className="text-xs text-bw-muted">Processed</div>
                <div className="text-lg font-bold">{enrichResult.totalProcessed}</div>
              </div>
              <div>
                <div className="text-xs text-bw-primary">Enriched (Batch)</div>
                <div className="text-lg font-bold text-bw-primary">{enrichResult.enriched}</div>
              </div>
              {cumulativeEnriched > enrichResult.enriched && (
                <div>
                  <div className="text-xs text-bw-primary">Total Enriched</div>
                  <div className="text-lg font-bold text-bw-primary">{cumulativeEnriched}</div>
                </div>
              )}
              <div>
                <div className="text-xs text-bw-muted">Skipped</div>
                <div className="text-lg font-bold text-bw-muted">{enrichResult.skipped}</div>
              </div>
              <div>
                <div className="text-xs text-urgent">Failed</div>
                <div className="text-lg font-bold text-urgent">{enrichResult.failed}</div>
              </div>
            </div>

            {/* Progress bar for enrichment */}
            {enrichResult.totalAvailable > 0 && enrichResult.lastOffset !== undefined && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-bw-muted mb-1">
                  <span>Progress: {enrichResult.lastOffset} / {enrichResult.totalAvailable} contracts</span>
                  <span>{Math.round((enrichResult.lastOffset / enrichResult.totalAvailable) * 100)}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-bw-primary transition-all duration-300"
                    style={{ width: `${Math.min((enrichResult.lastOffset / enrichResult.totalAvailable) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Resume Enrichment Button */}
            {enrichResult.canResume && (
              <div className="bg-bw-primary-tint border border-purple-300 rounded-lg p-3 mb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-medium text-bw-primary">More contracts to enrich</span>
                    <span className="text-sm text-bw-primary ml-2">
                      ({enrichResult.totalAvailable - enrichResult.nextResumeOffset} remaining)
                    </span>
                  </div>
                  <button
                    onClick={handleResumeEnrich}
                    disabled={isEnriching}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isEnriching
                        ? 'bg-gray-300 cursor-not-allowed text-bw-muted'
                        : 'bg-bw-primary hover:brightness-110 text-white'
                    }`}
                  >
                    {isEnriching ? (
                      <>
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Resuming...
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3" />
                        Resume
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {enrichResult.errors?.length > 0 && (
              <div className="mt-3 text-sm text-urgent">
                {enrichResult.errors.slice(0, 3).map((err: string, i: number) => (
                  <div key={i}>{err}</div>
                ))}
                {enrichResult.errors.length > 3 && (
                  <div>...and {enrichResult.errors.length - 3} more errors</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Logs */}
      {logs.length > 0 && (
        <div className="bg-gray-900 rounded-xl p-4">
          <h3 className="text-sm font-medium text-bw-muted mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Import Logs
          </h3>
          <div className="font-mono text-xs text-green-400 max-h-48 overflow-y-auto">
            {logs.map((log, i) => (
              <div key={i} className="py-0.5">{log}</div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Jobs */}
      {recentJobs.length > 0 && (
        <div className="bg-bw-surface rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-bw-primary" />
            Recent Import Jobs
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3">Job ID</th>
                  <th className="text-left py-2 px-3">Completed</th>
                  <th className="text-right py-2 px-3">Imported</th>
                  <th className="text-right py-2 px-3">Failed</th>
                  <th className="text-right py-2 px-3">Progress</th>
                  <th className="text-right py-2 px-3">Duration</th>
                  <th className="text-center py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.map(job => (
                  <tr key={job.job_id} className="border-b hover:bg-bw-surface-subtle">
                    <td className="py-2 px-3 font-mono text-xs">{job.job_id.slice(0, 20)}...</td>
                    <td className="py-2 px-3">{new Date(job.completed_at).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right text-success font-medium">
                      {job.result?.importedRecords || 0}
                    </td>
                    <td className="py-2 px-3 text-right text-urgent">
                      {job.result?.failedRecords || 0}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {job.result?.lastOffset && job.result?.totalRecords ? (
                        <span className={job.result?.canResume ? 'text-bw-primary' : 'text-bw-muted'}>
                          {Math.round((job.result.lastOffset / job.result.totalRecords) * 100)}%
                        </span>
                      ) : (
                        <span className="text-bw-muted">-</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {((job.duration_ms || 0) / 1000).toFixed(1)}s
                    </td>
                    <td className="py-2 px-3 text-center">
                      {job.result?.canResume && (
                        <button
                          onClick={() => handleRunImport(job.result.nextResumeOffset, job.job_id)}
                          disabled={isRunning}
                          className="text-xs px-2 py-1 bg-bw-primary-tint text-bw-primary rounded hover:bg-blue-200 disabled:opacity-50"
                          title={`Resume from offset ${job.result.nextResumeOffset}`}
                        >
                          Resume
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default SAMBatchImport;
